import {createStyles} from '@mantine/core';
import React, {ReactElement} from 'react';

import {assert} from 'Asserts';
import {HEADER_COLUMNS, HeaderInfo, NOT_SORTABLE} from 'relevant-items/RelevantItemsHeaderColumns';
import {
  getClampedPageOffset,
  getSortedRelevantItemRowData,
  matchModifiedAt,
  matchName,
  matchProjectFolder,
  matchReference,
  RelevantRowData,
} from 'relevant-items/shared/RelevantItemsTableUtils';
import {Cell, CellProps, Column, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell from 'ui-library/simple-table/SortableHeaderCell';
import useSimpleTableSortState, {SimpleTableSortState} from 'ui-library/simple-table/useSimpleTableSortState';
import * as DateUtil from 'util/DateUtil';
import filterItemsToSearchQuery from 'util/filterItemsToSearchQuery';

const useStyles = createStyles((theme) => ({
  grayHoverHeaderCell: {
    transition: 'background-color 0.1s',
    backgroundColor: theme.colors.gray[0],
    '&:hover': {
      backgroundColor: theme.colors.gray[2],
    },
  },
}));

const SEARCH_MATCHERS = [matchName, matchModifiedAt, matchProjectFolder, matchReference];

const DEFAULT_SORT_STATE: Exclude<SimpleTableSortState, null> = {
  columnKey: HEADER_COLUMNS.modifiedAt.sortIndex,
  sortDirection: 'arrow-up',
};

export interface RelevantItemsTableProps {
  /** Rows for the currently active filter option, before search filtering and sorting. */
  rowData: Array<RelevantRowData>;
  searchQuery: string;
  pageOffset: number;
  setPageOffset: (pageOffset: number) => void;
  /** Unique id used to persist sort state; pass the entity or container ID. */
  sortStateId: string;
  /** @default false */
  isCompact?: boolean;
  /** @default 100 */
  paginationLimit?: number;
}

/**
 * Table part of a relevant items section, shared by entity and container relevant items.
 *
 * Meant for internal use by the per-domain relevant items tables (RelevantItemsSectionTable and
 * StorableRelevantItemsSectionTable), which own turning their GraphQL data into rows.
 */
export default function RelevantItemsTable({
  rowData,
  searchQuery,
  pageOffset,
  setPageOffset,
  sortStateId,
  isCompact = false,
  paginationLimit = 100,
}: RelevantItemsTableProps): JSX.Element {
  const {classes} = useStyles();
  const {sortState, getHeaderCellProps} = useSimpleTableSortState(sortStateId);

  const filteredRowData = filterItemsToSearchQuery(rowData, searchQuery, SEARCH_MATCHERS);
  const sortedRowData = getSortedRelevantItemRowData(filteredRowData, sortState ?? DEFAULT_SORT_STATE);
  const clampedPageOffset = getClampedPageOffset(pageOffset, sortedRowData.length, paginationLimit);
  const numRows = Math.min(sortedRowData.length - clampedPageOffset, paginationLimit);

  function renderHeader(headerInfo: HeaderInfo): ReactElement<CellProps> {
    assert(headerInfo.sortIndex !== NOT_SORTABLE, 'Expected header to have sortable index');

    return (
      <SortableHeaderCell {...getHeaderCellProps(headerInfo.sortIndex)}>{headerInfo.name}</SortableHeaderCell>
    );
  }

  return (
    <Table
      emptyTableText="No relevant items were found."
      numRows={numRows}
      pagination={{offset: clampedPageOffset, limit: paginationLimit, total: sortedRowData.length}}
      setOffset={setPageOffset}
      isCompact={isCompact}
    >
      <Column
        header={renderHeader(HEADER_COLUMNS.item)}
        renderCell={(rowIndex) => <Cell>{sortedRowData[clampedPageOffset + rowIndex].chip}</Cell>}
      />
      <Column
        header={<Cell className={classes.grayHoverHeaderCell}>{HEADER_COLUMNS.reference.name}</Cell>}
        renderCell={(rowIndex) => <Cell>{sortedRowData[clampedPageOffset + rowIndex].referencedInText}</Cell>}
      />
      <Column
        header={renderHeader(HEADER_COLUMNS.modifiedAt)}
        renderCell={(rowIndex) => (
          <Cell>
            {sortedRowData[clampedPageOffset + rowIndex].modifiedAt &&
              DateUtil.readableTime(sortedRowData[clampedPageOffset + rowIndex].modifiedAt)}
          </Cell>
        )}
      />
      <Column
        header={renderHeader(HEADER_COLUMNS.projectLocation)}
        renderCell={(rowIndex) => <Cell>{sortedRowData[clampedPageOffset + rowIndex].location}</Cell>}
      />
    </Table>
  );
}
